import type { SignInPage } from '@server/shared-sign-in';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Separator } from '@/_shared/components/ui/separator';

import type { LoginCredentials, MfaChallenge } from '../../lib/rudderstack-auth-client';
import { signupPath } from '../../lib/signup-invite';
import type { NativeSignInConfig } from '../../types';
import { FederatedStartButton } from './FederatedStartButton';
import { GoogleGlyph } from './GoogleGlyph';
import { MfaCodeStep } from './MfaCodeStep';
import { MfaSetupRequired } from './MfaSetupRequired';
import { PasswordSignInForm } from './PasswordSignInForm';
import { SsoSignInForm } from './SsoSignInForm';
import { TextLink } from './TextLink';

type Step =
  | { kind: 'password'; email?: string }
  | { kind: 'mfa'; credentials: LoginCredentials; challenge: MfaChallenge }
  | { kind: 'mfa_setup'; email?: string }
  | { kind: 'sso' };

/** Lookout's own sign-in (ACT2-871), shared by `/login` and `/invite/:id`. */
export function NativeSignIn({
  config,
  returnTo,
  invite = false,
  fromWebApp = false,
  page = 'login',
  onNewAttempt,
}: {
  config: NativeSignInConfig;
  returnTo: string | null;
  /** Marks a Google, SSO or email sign-up as coming from a Lookout invite. */
  invite?: boolean;
  /** The visit came from the RudderStack web app's sidebar. A Google or SSO start carries it to analytics. */
  fromWebApp?: boolean;
  /** The page that shows this sign-in. A Google or SSO error returns to it. */
  page?: SignInPage;
  /** Called when Google starts, so the page can hide the error of an earlier attempt. */
  onNewAttempt?: () => void;
}): React.ReactElement {
  const [step, setStep] = useState<Step>({ kind: 'password' });
  // Leaving the MFA step drops the password it held.
  const backToPassword = (email?: string): void => setStep({ kind: 'password', email });

  if (step.kind === 'mfa') {
    const { credentials } = step;
    return (
      <MfaCodeStep
        apiBaseUrl={config.apiBaseUrl}
        credentials={credentials}
        challenge={step.challenge}
        returnTo={returnTo}
        onSetupRequired={() =>
          setStep({ kind: 'mfa_setup', email: 'email' in credentials ? credentials.email : undefined })
        }
        onBack={() => backToPassword('email' in credentials ? credentials.email : undefined)}
      />
    );
  }
  if (step.kind === 'mfa_setup') {
    const { email } = step;
    return <MfaSetupRequired webAppUrl={config.webAppUrl} onBack={() => backToPassword(email)} />;
  }
  if (step.kind === 'sso') {
    return (
      <SsoSignInForm
        apiBaseUrl={config.apiBaseUrl}
        hostedUi={config.hostedUi}
        returnTo={returnTo}
        invite={invite}
        fromWebApp={fromWebApp}
        page={page}
        onBack={() => backToPassword()}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 text-left">
      <PasswordSignInForm
        apiBaseUrl={config.apiBaseUrl}
        returnTo={returnTo}
        defaultEmail={step.email}
        onChallenge={(credentials, challenge) => setStep({ kind: 'mfa', credentials, challenge })}
        onSetupRequired={(email) => setStep({ kind: 'mfa_setup', email })}
      />
      {config.federated && (
        <>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <Separator className="flex-1" />
            or
            <Separator className="flex-1" />
          </div>
          <FederatedStartButton
            hostedUi={config.hostedUi}
            options={{ returnTo, invite, fromWebApp, page }}
            onStart={onNewAttempt}
          >
            <GoogleGlyph />
            {/* On the invite page, Google also creates the account of a new invitee. */}
            {page === 'invite' ? 'Continue with Google' : 'Sign in with Google'}
          </FederatedStartButton>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full gap-2"
            Icon={<KeyRound />}
            onClick={() => setStep({ kind: 'sso' })}
          >
            Sign in with SSO
          </Button>
        </>
      )}
      <p className="text-center text-xs text-muted-foreground">
        New to RudderStack? <TextLink to={signupPath({ invite, returnTo })}>Create an account</TextLink>
      </p>
    </div>
  );
}
